import { useCallback, useMemo, useSyncExternalStore } from "react";
import { useExpiryTick } from "@/hooks/useExpiryTick";
import { classifyConflict } from "./conflicts";
import { ApiError } from "./errors";
import { newIdempotencyKey } from "./idempotency";
import { wasNotDispatched } from "./sentRequest";
import { isDefinitiveRejection } from "./useIdempotencyKey";

/**
 * Lifecycle of one create submission that must never run twice by accident.
 *
 * idle ── submit ──▶ pending ── success ─────────────▶ idle (cleared)
 *                       │      definitive rejection ─▶ idle (cleared)
 *                       └──── unknown outcome ──────▶ unresolved
 *
 * unresolved: the request may have committed (lost response, 5xx, 429,
 * timeout, "still in progress"). The exact payload — including generated
 * secrets such as passwords — and its Idempotency-Key are kept. A new key is
 * NOT minted while unresolved: the only ways forward are retrying the original
 * payload (same key, so the server replays the first result) or an explicit
 * "start new" by the operator.
 *
 * maybeExists: a retry of the original came back as a duplicate ("already
 * exists"). The first attempt most likely committed, so the retained data
 * (e.g. the password it set) stays visible until the operator starts new.
 *
 * State lives in a module-level store keyed by scope, so it survives closing
 * and reopening the form (and is isolated per tenant/server via the scope).
 */
export type AttemptStatus = "idle" | "pending" | "unresolved" | "maybeExists";

export interface AttemptState<P> {
  status: AttemptStatus;
  /** The payload of the current or retained submission. */
  payload: P | null;
  fingerprint: string | null;
  key: string | null;
  /** True when the pending request is a retry of a retained attempt. */
  retrying: boolean;
  /** When the first request with this key was sent (ms): replays have a window. */
  startedAt?: number;
  /**
   * A request still in flight from before retained data was cleared (an
   * earlier session). It keeps the scope busy, so a new submission can't be
   * mistaken for it when it settles, but it holds no data and its outcome is
   * never retained.
   */
  orphaned?: boolean;
}

export type AttemptEvent<P> =
  | { type: "submit"; payload: P; fingerprint: string; key: string; at?: number }
  | { type: "success" }
  | { type: "failure"; error: unknown }
  | { type: "startNew" };

export const IDLE: AttemptState<never> = {
  status: "idle",
  payload: null,
  fingerprint: null,
  key: null,
  retrying: false,
};

/**
 * The backend keeps idempotency records for 24 hours
 * (internal/api/handlers/iam_support.go idempotencyTTL); after that the same
 * key would run the operation again instead of replaying it. Retries are only
 * offered inside this window (with a margin for clock skew).
 */
export const REPLAY_WINDOW_MS = 23 * 60 * 60 * 1000;

/** A locked attempt too old to replay safely: only "start new" remains. */
export function isReplayExpired(state: AttemptState<unknown>, now = Date.now()): boolean {
  return isLocked(state) && state.startedAt !== undefined && now - state.startedAt > REPLAY_WINDOW_MS;
}

/** Thrown by `begin` when an unresolved attempt blocks a different payload. */
export class AttemptLockedError extends Error {
  constructor() {
    super("an earlier attempt's outcome is unknown; retry it or start new");
    this.name = "AttemptLockedError";
  }
}

export function isLocked(state: AttemptState<unknown>): boolean {
  return state.status === "unresolved" || state.status === "maybeExists";
}

export function fingerprintOf(payload: unknown): string {
  return JSON.stringify(payload ?? null);
}

/**
 * The key to send for `payload`, or null when the state forbids sending it
 * (a retained attempt with a different payload, or a request already pending).
 */
export function keyFor<P>(state: AttemptState<P>, fingerprint: string): string | null {
  if (state.status === "pending") return null;
  if (isLocked(state)) return state.fingerprint === fingerprint ? state.key : null;
  return null;
}

export function attemptReducer<P>(state: AttemptState<P>, event: AttemptEvent<P>): AttemptState<P> {
  switch (event.type) {
    case "submit": {
      if (state.status === "pending") return state;
      if (isLocked(state)) {
        // Only the retained payload may be resent, with its own key.
        if (state.fingerprint !== event.fingerprint) return state;
        return { ...state, status: "pending", retrying: true };
      }
      return {
        status: "pending",
        startedAt: event.at ?? Date.now(),
        payload: event.payload,
        fingerprint: event.fingerprint,
        key: event.key,
        retrying: false,
      };
    }
    case "success":
      return IDLE as AttemptState<P>;
    case "failure": {
      if (state.status !== "pending") return state;
      if (event.error instanceof AttemptLockedError) return state;
      if (state.orphaned) return IDLE as AttemptState<P>;
      // Nothing left the browser (e.g. the token refresh failed first): a
      // first attempt changed nothing. A retry keeps the earlier attempt's
      // lock, since that one may still have gone through.
      if (wasNotDispatched(event.error))
        return state.retrying ? { ...state, status: "unresolved" } : (IDLE as AttemptState<P>);
      if (!isDefinitiveRejection(event.error)) return { ...state, status: "unresolved" };
      // A retry of an attempt that may have committed, answered "already
      // exists": the first attempt most likely went through. Keep its data.
      if (state.retrying && classifyConflict(event.error) === "duplicate") {
        return { ...state, status: "maybeExists" };
      }
      // An idempotency_conflict means the key itself is unusable; any other
      // definitive rejection means the operation did not happen. Either way a
      // new submission is a new operation.
      return IDLE as AttemptState<P>;
    }
    case "startNew":
      return IDLE as AttemptState<P>;
  }
}

// --- store ------------------------------------------------------------------

const states = new Map<string, AttemptState<unknown>>();
const listeners = new Map<string, Set<() => void>>();

function read(scope: string): AttemptState<unknown> {
  return states.get(scope) ?? IDLE;
}

function dispatch(scope: string, event: AttemptEvent<unknown>) {
  const next = attemptReducer(read(scope), event);
  if (next === read(scope)) return;
  if (next.status === "idle") states.delete(scope);
  else states.set(scope, next);
  for (const listener of listeners.get(scope) ?? []) listener();
}

function subscribe(scope: string, listener: () => void) {
  let set = listeners.get(scope);
  if (!set) {
    set = new Set();
    listeners.set(scope, set);
  }
  set.add(listener);
  return () => {
    set.delete(listener);
  };
}

/** Current status of the attempt under `scope` (outside React). */
export function retainedAttemptStatus(scope: string): AttemptStatus {
  return read(scope).status;
}

/** The idempotency key of the attempt under `scope`, if any (outside React). */
export function retainedAttemptKey(scope: string): string | null {
  return read(scope).key;
}

// Other per-session stores of retained data (e.g. pending plan retries)
// register here so one call clears everything.
const extraClears = new Set<() => void>();
const extraHolds = new Set<() => boolean>();

/**
 * Register another store of retained data: `clear` drops it, `holds` (when
 * given) reports whether it currently keeps anything.
 */
export function onClearRetained(clear: () => void, holds?: () => boolean): void {
  extraClears.add(clear);
  if (holds) extraHolds.add(holds);
}

/** Is any retained attempt (or registered retained data) kept right now? */
export function hasRetainedData(): boolean {
  for (const state of states.values()) if (!state.orphaned) return true;
  for (const holds of extraHolds) if (holds()) return true;
  return false;
}

/**
 * Drop every retained attempt. Called whenever the session ends or changes
 * user: retained payloads can hold generated passwords and other people's
 * details, and their keys belong to the previous session.
 */
export function clearAllRetained(): void {
  epoch += 1;
  const scopes = [...states.keys()];
  for (const scope of scopes) {
    const state = states.get(scope);
    // A request still in flight keeps the scope busy until it settles (its
    // late success/failure must not land on a newer submission), but its
    // payload, key and any outcome are dropped.
    if (state?.status === "pending") states.set(scope, { ...IDLE, status: "pending", orphaned: true });
    else states.delete(scope);
  }
  for (const scope of scopes) for (const listener of listeners.get(scope) ?? []) listener();
  for (const clear of extraClears) clear();
  for (const listener of epochListeners) listener();
}

// Bumped by every clear. A callback of a request started before a clear (an
// earlier user's session) compares epochs and must not write retained data
// back under the new session.
let epoch = 0;
const epochListeners = new Set<() => void>();

export function retainedEpoch(): number {
  return epoch;
}

/** Re-renders when retained data is cleared; returns the current epoch. */
export function useRetainedEpoch(): number {
  return useSyncExternalStore(
    (listener) => {
      epochListeners.add(listener);
      return () => {
        epochListeners.delete(listener);
      };
    },
    retainedEpoch,
    retainedEpoch,
  );
}

/** Test-only: drop every retained attempt. */
export function __resetRetainedAttempts() {
  clearAllRetained();
  states.clear();
}

export interface RetainedAttempt<P> {
  status: AttemptStatus;
  /** Retained (or pending) payload; null when idle. */
  payload: P | null;
  /** Unresolved or maybeExists: a new key must not be minted. */
  locked: boolean;
  /** Locked, but older than the server's replay window: retrying could repeat it. */
  expired: boolean;
  /** True when `payload` is exactly the retained one (a retry is allowed). */
  matches: (payload: P) => boolean;
  /** True when submitting `payload` now is forbidden (locked and different). */
  blocks: (payload: P) => boolean;
  /**
   * Start a submission: returns the Idempotency-Key header for it. Throws
   * AttemptLockedError when an unresolved attempt blocks a different payload.
   */
  begin: (payload: P) => Record<string, string>;
  succeed: () => void;
  fail: (error: unknown) => void;
  /** Operator explicitly abandons the retained attempt. */
  startNew: () => void;
}

/**
 * Track a create submission under `scope` (e.g. `createUser:<tenant>`). Pass a
 * scope that identifies where the result would land, so an attempt is only
 * ever retried into the same place.
 */
export function useRetainedAttempt<P>(scope: string): RetainedAttempt<P> {
  const sub = useCallback((listener: () => void) => subscribe(scope, listener), [scope]);
  const snap = useCallback(() => read(scope), [scope]);
  const state = useSyncExternalStore(sub, snap, snap) as AttemptState<P>;

  const begin = useCallback(
    (payload: P) => {
      const fingerprint = fingerprintOf(payload);
      const current = read(scope);
      // One submission at a time per scope (a remounted form must not send a
      // second request under the first one's key).
      if (current.status === "pending") throw new AttemptLockedError();
      if (isLocked(current) && current.fingerprint !== fingerprint) throw new AttemptLockedError();
      // Past the server's replay window the same key would run it again.
      if (isReplayExpired(current)) throw new AttemptLockedError();
      const key = keyFor(current, fingerprint) ?? newIdempotencyKey();
      dispatch(scope, { type: "submit", payload, fingerprint, key });
      const now = read(scope);
      return { "Idempotency-Key": now.key ?? key };
    },
    [scope],
  );

  // Re-render when the replay window closes, so the notice and the submit
  // guards turn "expired" together while mounted.
  const deadline =
    isLocked(state) && state.startedAt !== undefined
      ? new Date(state.startedAt + REPLAY_WINDOW_MS + 1).toISOString()
      : null;
  useExpiryTick([deadline]);
  const expired = isReplayExpired(state);

  return useMemo(
    () => ({
      status: state.status,
      payload: state.payload,
      locked: isLocked(state),
      expired,
      matches: (payload: P) => state.fingerprint === fingerprintOf(payload),
      blocks: (payload: P) =>
        state.status === "pending" ||
        isReplayExpired(state) ||
        (isLocked(state) && state.fingerprint !== fingerprintOf(payload)),
      begin,
      succeed: () => dispatch(scope, { type: "success" }),
      fail: (error: unknown) => dispatch(scope, { type: "failure", error }),
      startNew: () => dispatch(scope, { type: "startNew" }),
    }),
    [state, scope, begin, expired],
  );
}

/** Does this error mean an earlier, retained attempt blocked the submit? */
export function isAttemptLocked(error: unknown): boolean {
  return error instanceof AttemptLockedError;
}

/** Re-exported for callers that classify after a failure. */
export { ApiError };
